// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';

export type AnalysisHours = 24 | 168;

/** 趋势数据点 - 用于迷你折线图 */
export interface TrendPoint {
  x: string;
  y: number;
}

/** 账户数据（含使用统计） */
export interface AccountData {
  balance: number;
  ledgerBalance?: number;
  availableBalance?: number;
  totalSpent: number;
  requestCount: number;
  threadCount: number;
}

/** Token 消耗 */
export interface TokenConsumption {
  credits: number;
  tokens: number;
  creditsTrend: TrendPoint[];
  tokensTrend: TrendPoint[];
}

/** 图片消耗 */
export interface ImageConsumption {
  credits: number;
  count: number;
  creditsTrend: TrendPoint[];
  countTrend: TrendPoint[];
}

/** 视频消耗 */
export interface VideoConsumption {
  credits: number;
  seconds: number;
  creditsTrend: TrendPoint[];
  secondsTrend: TrendPoint[];
}

/** 音频消耗 */
export interface AudioConsumption {
  credits: number;
  characters: number;
  creditsTrend: TrendPoint[];
  charactersTrend: TrendPoint[];
}

/** 工具统计数据点 - 用于堆叠柱状图 */
export interface ToolStatPoint {
  timestamp: string;
  toolId?: number | null;
  toolName: string;
  toolType?: string | null;
  value: number;
}

/** 工具分析数据 */
export interface ToolAnalysisData {
  consumptionByTool?: ToolStatPoint[];
  callCountByTool?: ToolStatPoint[];
}

/** 工具分析数据 - 多时间范围聚合（24h/7d） */
export interface ToolAnalysisDataRanges {
  /** 24 小时 */
  h24?: ToolAnalysisData;
  /** 7 天 */
  d7?: ToolAnalysisData;

  /** 兼容：以 hours 命名 */
  hours24?: ToolAnalysisData;
  hours168?: ToolAnalysisData;

  /** 兼容：以 hour/day 命名 */
  hour24?: ToolAnalysisData;
  day7?: ToolAnalysisData;

  /** 兼容：以 lastXX 命名 */
  last24Hours?: ToolAnalysisData;
  last7Days?: ToolAnalysisData;

  /** 兼容：以字符串数字命名 */
  '24'?: ToolAnalysisData;
  '168'?: ToolAnalysisData;

  /** 兼容：以 24h / 7d 命名 */
  '24h'?: ToolAnalysisData;
  '7d'?: ToolAnalysisData;
}

/** 数据看板数据 */
export interface DashboardData {
  accountData: AccountData;
  tokenConsumption: TokenConsumption;
  imageConsumption: ImageConsumption;
  videoConsumption: VideoConsumption;
  audioConsumption: AudioConsumption;
  /** 兼容：旧字段（单时间范围）或新字段（多时间范围） */
  toolAnalysisData?: ToolAnalysisData | ToolAnalysisDataRanges;

  /** 新字段：一次返回 24h / 7d（优先使用） */
  toolAnalysisData24h?: ToolAnalysisData;
  toolAnalysisData7d?: ToolAnalysisData;
}

export function resolveToolAnalysisData(
  dashboardData: DashboardData | undefined,
  hours: AnalysisHours,
): ToolAnalysisData | undefined {
  if (!dashboardData) return undefined;

  if (hours === 24 && dashboardData.toolAnalysisData24h) return dashboardData.toolAnalysisData24h;
  if (hours === 168 && dashboardData.toolAnalysisData7d) return dashboardData.toolAnalysisData7d;

  const container = dashboardData.toolAnalysisData;
  if (!container) return undefined;

  // 单时间范围结构
  const asSingle = container as ToolAnalysisData;
  if (
    Array.isArray(asSingle.consumptionByTool) ||
    Array.isArray(asSingle.callCountByTool)
  ) {
    return asSingle;
  }

  // 多时间范围结构
  const ranges = container as ToolAnalysisDataRanges;
  if (hours === 24) {
    return (
      ranges.h24 ??
      ranges.hour24 ??
      ranges.last24Hours ??
      ranges.hours24 ??
      ranges['24h'] ??
      ranges['24']
    );
  }
  return (
    ranges.d7 ??
    ranges.day7 ??
    ranges.last7Days ??
    ranges.hours168 ??
    ranges['7d'] ??
    ranges['168']
  );
}

/**
 * 获取数据看板概览数据。
 * 当前链路：website-front `/api/dashboard/data` -> website-end `/dashboard/data`
 * -> llm-proxy-server `/internal/dashboard/data`
 */
export async function fetchDashboardData(
  options?: { [key: string]: any },
) {
  const res = await request<any>('/api/dashboard/data', {
    method: 'GET',
    ...(options || {}),
  });
  return res;
}
